import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import './Appointments.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Tab, Card, Rating, CircularProgress } from '@mui/material';
// ★ ‏`TabPanel` הוסר: הפאנלים מרונדרים ידנית כדי שיוכלו לזוז יחד (ראה `.tab-track`).
import { TabContext, TabList } from '@mui/lab';
import ScrollList from './ScrollList';
import AddIcon from '@mui/icons-material/Add';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import { useForwardChevron } from '../../i18n/DirectionalIcons';
import EventRoundedIcon from '@mui/icons-material/EventRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import moment from 'moment';
import { startForm, submitRating } from '../../redux/actions/dataActions';
import AppFooterMobile from '../layout/footer/AppFooterMobile';
import { useI18n, useT } from '../../i18n';

// Visit status reported back by the queue/kiosk system (visitors.visit_status). Citizen-facing
// colors (mirrors the dashboard's status colors). Empty status → no badge.
// Colors match the dashboard status colors exactly (models/Visitors.php displayFields).
// ★ F1 — התוויות עברו ל-`visitStatus.*` בקובצי-השפה; כאן נשאר רק הצבע, שאינו
//   תלוי-שפה. המפתח (`arrived` וכו') הוא הקוד שהשרת מחזיר.
const VISIT_STATUS_COLOR = {
  arrived:      '#3a87c8',
  in_treatment: '#e0941a',
  treated:      '#36b877',
  no_show:      '#9aa7b8',
  abandoned:    '#c0504d',
};

// C8 — service rating on a PAST appointment: star picker (+ optional comment). Once rated (or already
// rated per the feed's `rating`), it shows the given stars read-only WITH an "edit" link so a citizen
// can change their rating (the API upserts, so re-submitting overwrites the previous one).
function AppointmentRating({ visitor, onRate }) {
  const t = useT();
  const existing = visitor.rating; // { rating, comment } | null (from the appointments feed)
  const [value, setValue] = useState(existing ? existing.rating : 0);
  const [comment, setComment] = useState(existing ? (existing.comment || '') : '');
  const [done, setDone] = useState(!!existing);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(false);

  if (done) {
    // compact: just the given stars + an edit link. The comment isn't shown inline (no room in the
    // card) — clicking "שינוי דירוג" reopens the picker with the comment field (prefilled) below.
    return (
      <div className="appt-rating appt-rating--done">
        <span className="appt-rating-lbl">{t('rating.rated')}</span>
        <Rating value={value} readOnly size="small" />
        <button type="button" className="appt-rating-edit" onClick={() => setDone(false)}>
          {t('rating.change')}
        </button>
      </div>
    );
  }

  const submit = async () => {
    if (!value || busy) return;
    setBusy(true); setErr(false);
    try { await onRate(visitor.id, value, comment); setDone(true); }
    catch (e) { setErr(true); }
    finally { setBusy(false); }
  };

  // cancel an EDIT of an already-submitted rating: revert the fields and go back to the read-only view.
  const cancel = () => {
    setValue(existing ? existing.rating : 0);
    setComment(existing ? (existing.comment || '') : '');
    setErr(false);
    setDone(!!existing);
  };

  return (
    <div className="appt-rating">
      <div className="appt-rating-row">
        <span className="appt-rating-lbl">{t('rating.rateUs')}</span>
        <Rating value={value} onChange={(e, v) => setValue(v || 0)} size="small" />
      </div>
      {(value > 0 || existing) && (
        <div className="appt-rating-send">
          <input
            className="appt-rating-cmt"
            type="text"
            placeholder={t('rating.commentPlaceholder')}
            value={comment}
            maxLength={500}
            onChange={(e) => setComment(e.target.value)}
          />
          {value > 0 && (
            <button type="button" className="appt-rating-btn" onClick={submit} disabled={busy}>
              {busy ? t('common.sending') : t('common.send')}
            </button>
          )}
          {existing && (
            <button type="button" className="appt-rating-cancel" onClick={cancel} disabled={busy}>
              {t('common.cancel')}
            </button>
          )}
        </div>
      )}
      {err && <div className="appt-rating-err">{t('rating.error')}</div>}
    </div>
  );
}

function Appointments() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t, dir } = useI18n();
  const FwdChevron = useForwardChevron();

  /* ★★ הודעה שהועברה בניווט (‏`navigate('/', { state: { notice } })`) —
     היום רק מ-`Form.js`, כשהמחלקה שהאזרח היה בתוכה נסגרה באמצע התהליך.
     ⚠️ נקראת **פעם אחת** לתוך state מקומי, ומיד נמחקת מ-`history.state`:
        בלי המחיקה היא הייתה צצה שוב בכל "אחורה" של הדפדפן אל המסך הזה,
        הרבה אחרי שהאירוע כבר לא רלוונטי. */
  const [notice, setNotice] = useState(location.state?.notice || '');
  useEffect(() => {
    if (!location.state?.notice) { return; }
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Default to future tab; RTL visual order: future on right, past on left
  const [currentTab, setCurrentTab] = useState('future');
  /* ★★ A7 — ‏`slideDir` שימש בעבר לבחירת אנימציית-כניסה; היום הוא רק דגל
     "כבר הייתה החלפה", שמפעיל את ה-transition על המסלול. בטעינה הראשונה הוא
     null בכוונה, כדי שהמסלול ייצב במקומו **בלי** אנימציית-פתיחה. */
  const [slideDir, setSlideDir] = useState(null);
  const touchX = useRef(null);
  const touchY = useRef(null);
  const swipeRef = useRef(null);
  const swiped = useRef(false); // fire the tab-change once per gesture

  /* ★★ הזזת המסלול. שני הפאנלים יושבים זה לצד זה ברוחב 100% כל אחד; המסלול
     כולו מוזז ב-100% כדי להביא את השני למסך.
     ⚠️ הסימן תלוי-כיווניות: ב-RTL הפאנל הראשון ב-DOM יושב מימין, ולכן כדי
        להביא את השני (שנמצא משמאלו) צריך להזיז **ימינה** (חיובי); ב-LTR
        להפך. קיבוע הסימן היה שובר את המעבר באנגלית/רוסית. */
  const trackOffset = currentTab === 'past' ? (dir === 'rtl' ? 100 : -100) : 0;

  const changeTab = (val) => {
    if (!val || val === currentTab) return;
    setSlideDir(val);
    setCurrentTab(val);
  };
  // rubber-band nudge when swiping past the edge tab (no further tab in that direction)
  const edgeBounce = (dir) => {
    const el = swipeRef.current;
    if (!el || !el.animate) return;
    const to = dir === 'left' ? -22 : 22;
    el.animate(
      [{ transform: 'translateX(0)' }, { transform: `translateX(${to}px)` }, { transform: 'translateX(0)' }],
      { duration: 340, easing: 'cubic-bezier(.36,.07,.19,.97)' }
    );
  };
  const onTouchStart = (e) => {
    touchX.current = e.touches[0].clientX;
    touchY.current = e.touches[0].clientY;
    swiped.current = false;
  };
  // Decide DURING the drag (not on touchend) — over a vertically-scrollable list the
  // browser often eats/aborts touchend, which made the swipe feel unreliable. As soon as
  // the movement is clearly horizontal we act, once per gesture.
  const onTouchMove = (e) => {
    if (touchX.current == null || swiped.current) return;
    const dx = e.touches[0].clientX - touchX.current;
    const dy = e.touches[0].clientY - touchY.current;
    if (Math.abs(dx) < 42 || Math.abs(dx) <= Math.abs(dy)) return; // need a mostly-horizontal drag
    swiped.current = true;
    /* ★ כיוון ההחלקה (בקשת-משתמש 27/07 — היה הפוך): **משמאל לימין ⇒ "תורים שעברו"**,
       ומימין לשמאל ⇒ "תורים קרובים" — אותה מחווה שנקבעה בלוח קביעת-התורים בניהול,
       כדי שההרגל יהיה אחד בכל המערכת. (הכרטיסיות ב-RTL: קרובים בימין, שעברו בשמאל.)
       ★★ ב-LTR סדר-הכרטיסיות מתהפך (קרובים בשמאל), ולכן גם המחווה חייבת
          להתהפך — אחרת החלקה באנגלית הייתה מושכת לכיוון ההפוך מהתנועה. */
    const target = (dir === 'rtl' ? dx > 0 : dx < 0) ? 'past' : 'future';
    if (target === currentTab) edgeBounce(dx < 0 ? 'left' : 'right'); // already at the edge
    else changeTab(target);
  };
  const onTouchEnd = () => {
    touchX.current = null;
    swiped.current = false;
  };

  const { user } = useSelector((state) => state.auth);
  const visitors = user?.visitors || [];
  /* ★ טעינה מול "באמת ריק": בטעינת-האתר/רענון, `user/me` עדיין בדרך ו-`user`
     הוא `undefined`, ולכן הרשימה ריקה ומצב-הריק ("אין לך תורים קרובים") הבהב
     לשנייה עד שהנתונים הגיעו (דיווח-משתמש 11/08). `visitors` הוא מערך **רק**
     אחרי ש-`user/me` חזר (login/USER_DATA מחזירים `generateOutput` עם `visitors`),
     ולכן זה הסימן המדויק ל"הנתונים הגיעו".
     ⚠️ **לא** להשתמש ב-`state.async.loading` — הוא נדלק גם בשליחת-דירוג/הזמנה
        והיה מהבהב את הרשימה כולה לספינר. */
  const dataLoaded = Array.isArray(user?.visitors);
  /* ★ אנטי-הבהוב: מציגים את מחוון-הטעינה **רק אם** הטעינה נמשכת מעל ~350ms.
     בטעינה מהירה (הרווח הנפוץ) המסך עובר ישר לתוכן בלי הבזק-ספינר בן-חלקיק-שנייה
     (דיווח-משתמש 11/08: "עדיין רואים את הספינר מתחת לשנייה"). */
  const [showLoader, setShowLoader] = useState(false);
  useEffect(() => {
    if (dataLoaded) { setShowLoader(false); return; }
    const timer = setTimeout(() => setShowLoader(true), 350);
    return () => clearTimeout(timer);
  }, [dataLoaded]);

  const pastVisitors = visitors
    .filter((v) => moment(v.appointed_time).isBefore(moment()))
    .sort(
      (a, b) =>
        moment(b.appointed_time).valueOf() - moment(a.appointed_time).valueOf()
    );
  const futureVisitors = visitors
    .filter((v) => moment(v.appointed_time).isAfter(moment()))
    .sort(
      (a, b) =>
        moment(a.appointed_time).valueOf() - moment(b.appointed_time).valueOf()
    );

  let hostname = window.location.hostname;
  if (hostname.startsWith("www.")) {
    hostname = hostname.substring(4);
  }
  const homeWrapMap = {
    "mayanot-hasharon.anstc.com": { home_wrap_margin_top: "0px" },
    "yamim.anstc.com":            { home_wrap_margin_top: "90px" },
    "miahcom.anstc.com":          { home_wrap_margin_top: "90px" },
    "migdal-haemeq.anstc.com":    { home_wrap_margin_top: "90px" },
    "shefaram.anstc.com":         { home_wrap_margin_top: "90px" },
    "elad.anstc.com":             { home_wrap_margin_top: "60px" },
    "yuvallim.anstc.com":         { home_wrap_margin_top: "60px" },
    "katsrin.anstc.com":          { home_wrap_margin_top: "60px" },
    "mayanotata.anstc.com":       { home_wrap_margin_top: "80px" },
  };
  const { home_wrap_margin_top } = homeWrapMap[hostname] || { home_wrap_margin_top: "0px" };

  // C7 "rebook": open a fresh booking with the past appointment's details (ID, name, department,
  // service) already filled — the citizen only picks a new date + time. The booking form reads
  // this from the navigation state (see Form.js) and jumps straight to the service step.
  const rebook = (data) => {
    dispatch(startForm());
    navigate('/form', {
      state: {
        rebook: {
          id_number: data.id_number,
          fname: data.fname,
          lname: data.lname,
          department_id: data.department_id,
          schedule_id: data.schedule_id,
          // ★ C2b — גם השדות הנוספים עוברים לתור החוזר: "קבע תור חוזר" נועד
          //   לחסוך הקלדה-מחדש, ואם "מספר נפשות" מתאפס האזרח ממלא אותו שוב
          //   בכל פעם. השרת שולח רק את מה שמותר לאזרח לראות, ולכן שדה-פנימי
          //   של הנציג לא ידלוף לכאן. הערכים ניתנים לשינוי בטופס כרגיל.
          extra_fields: data.extra_fields || {},
        },
      },
    });
  };

  // C8 — dispatch a rating submit (returns the thunk's promise so the card can await it).
  const rate = (visitorId, rating, comment) => dispatch(submitRating(visitorId, rating, comment));

  const AppointmentCard = ({ data, type }) => {
    /* ★★ מהתור עצמו, ורק בהיעדרו מרשימת-ההזמנה — אחרת סגירת מחלקה מוחקת
       את שמה מכרטיסי תורים קיימים. ראה ההסבר המלא ב-`Appointment.js`. */
    const department = data.department_title
      ? { title: data.department_title }
      : user?.departments?.find((d) => d.id === data.department_id);
    const isPast = type === 'past';
    const statusCode = (data.visit_status || '').trim();
    const statusColor = VISIT_STATUS_COLOR[statusCode];
    const when = moment(data.appointed_time);
    const service = data.schedule?.title;

    return (
      <div>
        <Card
          className={`appointment${isPast ? ' appointment--past' : ''}`}
          // past appointments have no detail screen (instructions are "before visit" only) → not clickable
          onClick={isPast ? undefined : () => navigate('/appointment/' + data.id)}
          style={isPast ? { cursor: 'default' } : undefined}
        >
          <div className="appt-head">
            <span className="appt-when">
              <span className="appt-chip appt-date">
                <EventRoundedIcon className="appt-chip-icon" />
                {when.format('DD/MM/YYYY')}
              </span>
              <span className="appt-chip appt-time">
                <AccessTimeRoundedIcon className="appt-chip-icon" />
                {when.format('HH:mm')}
              </span>
            </span>
            {/* Visit status reported by the queue system takes priority over the plain "הסתיים" tag */}
            {statusColor ? (
              <span className="appointment-status-badge" style={{ backgroundColor: statusColor }}>
                {t('visitStatus.' + statusCode)}
              </span>
            ) : (
              isPast && <span className="appointment-past-badge">{t('appointments.finished')}</span>
            )}
          </div>

          <div className="appt-service">
            {department?.title}{service && (<><span className="appt-sep">·</span>{service}</>)}
          </div>
          {/* name on the RIGHT + the action on the LEFT of the same row: on PAST cards "קבע תור חוזר"
              (above the rating), on FUTURE cards the "לפרטים ועריכה" hint. */}
          <div className="appt-person appt-person--action">
            <span className="appt-person-name">
              <PersonOutlineRoundedIcon className="appt-person-icon" />
              {data.fname} {data.lname}
            </span>
            {isPast ? (
              <button type="button" className="appt-rebook-btn" onClick={() => rebook(data)}>
                {t('appointments.rebook')} <ReplayRoundedIcon fontSize="small" />
              </button>
            ) : (
              <span className="appointment-details-hint">
                {t('appointments.detailsHint')} <FwdChevron fontSize="small" />
              </span>
            )}
          </div>

          {/* ★ C2b — השדות הנוספים שהלקוח סימן "כרטיס התור של האזרח".
              השרת כבר מסר תווית + ערך מפורמט (`extra_display`) ומסנן שם את
              מה שאסור לאזרח לראות — הפרונט רק מציג, בלי לשכפל כללים. */}
          {Array.isArray(data.extra_display) && data.extra_display.length > 0 && (
            <div className="appt-extra">
              {data.extra_display.map((x) => (
                <span className="appt-extra-item" key={x.label}>
                  <span className="appt-extra-label">{x.label}:</span> {x.value}
                </span>
              ))}
            </div>
          )}

          {/* C8 — rate the service on a past appointment */}
          {isPast && <AppointmentRating visitor={data} onRate={rate} />}
        </Card>
        <hr className="appointment_hr" />
      </div>
    );
  };

  return (
    <div style={{ marginTop: home_wrap_margin_top }} className="homeWrap">
      {/* ★★ הודעה שהגיעה עם הניווט חזרה לכאן — למשל אזרח שהיה באמצע הזמנה
          במחלקה שנסגרה (ראה ה-effect ב-`Form.js`). בלעדיה הוא היה "נזרק"
          למסך-התורים בלי שום הסבר, וזה נראה כמו תקלה.
          ★ ניתנת לסגירה, ונמחקת מ-`history.state` מיד עם ההצגה כדי שלא
            תחזור בכל חזרה-אחורה של הדפדפן. */}
      {notice && (
        <div className="ans-page-notice" role="status" aria-live="polite">
          <span className="ans-page-notice-txt">{notice}</span>
          <button
            type="button"
            className="ans-page-notice-close"
            aria-label={t('common.close')}
            onClick={() => setNotice('')}
          >
            ✕
          </button>
        </div>
      )}
      <div className="title_head">
        <h1>{t('appointments.title')}</h1>
        <Button
          variant="contained"
          className="new-appt-btn"
          onClick={() => {
            dispatch(startForm());
            navigate('/form');
          }}
          endIcon={<AddIcon />}
          disableElevation
        >
          {t('appointments.newAppointment')}
        </Button>
      </div>

      <TabContext value={currentTab}>
        {/*
          RTL tab order: in a right-to-left layout the visually "first" (rightmost)
          position is the primary/default. MUI TabList with dir=rtl places the first
          DOM child on the RIGHT. So "future" (the default tab) comes first in the DOM
          → it renders on the RIGHT, and "past" renders on the LEFT.
        */}
        <TabList
          className="tab-list"
          variant="fullWidth"
          onChange={(e, val) => changeTab(val)}
          TabIndicatorProps={{ style: { display: 'none' } }}
        >
          <Tab label={t('appointments.tabFuture')} value="future" />
          <Tab label={t('appointments.tabPast')} value="past" />
        </TabList>

        {/* ══════════════════════════════════════════════════════════════════
            A7 — החלפת-כרטיסיות בהחלקה.

            ★★ **מסלול-הזזה אמיתי במקום אנימציית-כניסה.** עד כה כל פאנל היה
               ‏`<TabPanel>` נפרד; MUI מפרק את הפאנל הלא-פעיל, ולכן היוצא
               **נעלם מיידית** והנכנס נכנס לבדו ב-45px — מה שנקרא כהבהוב ולא
               כהחלקה (דיווח-משתמש 27/07: "קופץ, אין אפקט החלקה").
            ⇒ שני הפאנלים מרונדרים תמיד, זה לצד זה, ו**המסלול כולו** מוזז ב-
              ‏`translateX` של 100%. כך היוצא והנכנס זזים יחד — זו התחושה של
              החלקה בין מסכים.
            ★ הכיווניות: ב-RTL הפאנל הראשון ב-DOM (קרובים) יושב **מימין**,
              ולכן מעבר ל"שעברו" מזיז את המסלול חיובית; ב-LTR הפוך. הסימן
              נגזר מ-`dir` ולא מקובע.
            ⚠️ ‏`aria-hidden` + `inert`-לייט (‏pointer-events) על הפאנל המוסתר,
               אחרת קורא-מסך ו-Tab היו נודדים לרשימה שאינה נראית.
            ══════════════════════════════════════════════════════════════════ */}
        <div className="tab-swipe" ref={swipeRef} onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
          <div
            className={'tab-track' + (slideDir ? ' tab-track--animate' : '')}
            style={{ transform: `translateX(${trackOffset}%)` }}
          >
            <div className="tab-pane" role="tabpanel" aria-hidden={currentTab !== 'future'}>
              {!dataLoaded ? (
                /* ★ טעינה — ספינר במקום מצב-הריק, כדי שלא יהבהב "אין תורים" עד
                   שהנתונים מגיעים. */
                showLoader && (
                  <div className="empty-state" aria-busy="true">
                    <CircularProgress size={30} sx={{ color: '#15a6e0' }} />
                    <p>{t('content.loadingData')}</p>
                  </div>
                )
              ) : futureVisitors.length === 0 ? (
                <div className="empty-state">
                  <EventBusyOutlinedIcon className="empty-state-icon" />
                  <p>{t('appointments.emptyFuture')}</p>
                  {/* No duplicate button here — the header button above handles booking */}
                </div>
              ) : (
                /* ★ `active`: רק הפאנל שעל המסך רשאי להדליק את הקלאס הגלובלי
                   של כפתור-הגלילה — ההסבר המלא ב-ScrollList. */
                <ScrollList active={currentTab === 'future'}>
                  {futureVisitors.map((v) => (
                    <AppointmentCard data={v} key={v.id} type="future" />
                  ))}
                </ScrollList>
              )}
            </div>
            <div className="tab-pane" role="tabpanel" aria-hidden={currentTab !== 'past'}>
              {!dataLoaded ? (
                showLoader && (
                  <div className="empty-state" aria-busy="true">
                    <CircularProgress size={30} sx={{ color: '#15a6e0' }} />
                    <p>{t('content.loadingData')}</p>
                  </div>
                )
              ) : pastVisitors.length === 0 ? (
                <div className="empty-state">
                  <EventBusyOutlinedIcon className="empty-state-icon" />
                  <p>{t('appointments.emptyPast')}</p>
                </div>
              ) : (
                <ScrollList active={currentTab === 'past'}>
                  {pastVisitors.map((v) => (
                    <AppointmentCard data={v} key={v.id} type="past" />
                  ))}
                </ScrollList>
              )}
            </div>
          </div>
        </div>
      </TabContext>

      {/* ★ פוטר (הצהרת-נגישות + זכויות + גרסה) גם אחרי ההתחברות, בתחתית "התורים שלי"
          (בקשת-משתמש 27/07: בלוגין הוא קיים ואחרי ההתחברות נעלם).
          ★ בזרימה — לא מקובע — כדי שיישב מתחת לרשימה ו**מתחת לכפתור-הגלילה הצף**
            (‏`.scroll-list-btn` תלוי 56px מתחת לרשימה). הריווח מוגדל רק כשהכפתור
            קיים בפועל, דרך הקלאס ש-ScrollList מדליק על ה-body — אחרת היה נפער
            חלל של 112px גם כשאין מה לגלול. מוסתר בדסקטופ (שם יש `.ans-desktop-footer`). */}
      <AppFooterMobile className="appt-footer" />
    </div>
  );
}

export default Appointments;
